/**
 * CategoryForm Component
 * 
 * Reusable form for creating and editing categories.
 * 
 * Features:
 * - Dynamic form fields based on category type
 * - Validation and error handling
 * - Parent category selection
 * - Color and icon pickers
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see specs/003-categorisation-feature/spec.md
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Category, CategoryType, CreateCategoryInput, UpdateCategoryInput } from '../../../../types/category.types';
import { ColorPicker } from './ColorPicker';
import { IconSelector } from './IconSelector';
import { ParentCategorySelector } from './ParentCategorySelector';
import { getCategoryIconComponent } from '../../../../utils/icon-utils';
import { Check, X } from 'lucide-react';

interface CategoryFormProps {
  category?: Category; // If provided, this is an edit form
  categories: Category[]; // All categories for parent selection
  onSubmit: (data: CreateCategoryInput | UpdateCategoryInput) => void;
  onCancel: () => void;
  loading?: boolean;
  initialType?: CategoryType; // Pre-select category type when adding new category
}

interface CategoryFormState {
  name: string;
  type: CategoryType;
  parentId?: string;
  color?: string;
  icon?: string;
  gstApplicable: boolean;
  deductible: boolean;
  note: string;
  excludeFromSpending: boolean;
}

export function CategoryForm({
  category,
  categories,
  onSubmit,
  onCancel,
  loading = false,
  initialType
}: CategoryFormProps): React.ReactElement {
  const isEdit = !!category;

  const initialState: CategoryFormState = {
    name: category?.name ?? '',
    type: category?.type ?? initialType ?? CategoryType.EXPENSE,
    parentId: category?.parentId ?? undefined,
    color: category?.color ?? undefined,
    icon: category?.icon ?? undefined,
    gstApplicable: category?.gstApplicable ?? false,
    deductible: category?.deductible ?? false,
    note: category?.note ?? '',
    excludeFromSpending: category?.excludeFromSpending ?? false,
  };

  const [formData, setFormData] = useState<CategoryFormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [inheritFromParent, setInheritFromParent] = useState<boolean>(() => {
    // Default to true when creating a new category with a parent, false when editing
    return !isEdit && !!initialState.parentId;
  });

  // Find parent category when parentId changes
  const parentCategory = useMemo(() => {
    if (!formData.parentId) return null;
    return categories.find(c => c.id === formData.parentId) || null;
  }, [formData.parentId, categories]);

  const colorUsageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    categories.forEach((cat) => {
      if (cat.color) {
        counts[cat.color] = (counts[cat.color] ?? 0) + 1;
      }
    });

    // When editing and the category already has the color selected, don't inflate the count
    if (isEdit && category?.color) {
      counts[category.color] = Math.max((counts[category.color] ?? 1) - 1, 0);
    }

    return counts;
  }, [categories, isEdit, category?.color]);

  // Reset parent when type changes (parent must match type)
  useEffect(() => {
    if (formData.type !== (category?.type ?? initialType)) {
      // Check if current parent matches new type
      if (formData.parentId) {
        const parent = categories.find(c => c.id === formData.parentId);
        if (parent && parent.type !== formData.type) {
          setFormData(prev => ({ ...prev, parentId: undefined }));
          setInheritFromParent(false);
        }
      }
    }
  }, [formData.type, category?.type, initialType, formData.parentId, categories]);

  // Apply inheritance when parent changes or inheritance is toggled on
  useEffect(() => {
    if (inheritFromParent && parentCategory) {
      setFormData(prev => ({
        ...prev,
        color: parentCategory.color ?? prev.color,
        icon: parentCategory.icon ?? prev.icon,
        gstApplicable: parentCategory.gstApplicable,
        deductible: parentCategory.deductible,
        excludeFromSpending: parentCategory.excludeFromSpending,
      }));
    }
  }, [inheritFromParent, parentCategory]);

  // Reset inheritance state when parent is deselected
  useEffect(() => {
    if (!formData.parentId) {
      setInheritFromParent(false);
    } else if (!isEdit) {
      // When creating a new category and a parent is selected, default to inheritance
      setInheritFromParent(true);
    }
  }, [formData.parentId, isEdit]);

  const handleInputChange = <K extends keyof CategoryFormState>(field: K, value: CategoryFormState[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as string]) {
      setErrors(prev => ({ ...prev, [field as string]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Category name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Category name must be 100 characters or less';
    }

    // Parent validation (if parent is selected, it must match type)
    if (formData.parentId) {
      const parent = categories.find(c => c.id === formData.parentId);
      if (!parent) {
        newErrors.parentId = 'Parent category not found';
      } else if (parent.type !== formData.type) {
        newErrors.parentId = `Parent category must be of type "${formData.type}"`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const submitData: CreateCategoryInput | UpdateCategoryInput = {
        name: formData.name.trim(),
        ...(isEdit ? {} : { type: formData.type }),
        ...(isEdit && formData.type !== category?.type ? { type: formData.type } : {}),
        parentId: formData.parentId || undefined,
        color: formData.color || undefined,
        icon: formData.icon || undefined,
        gstApplicable: formData.gstApplicable,
        deductible: formData.deductible,
        note: formData.note.trim() || undefined,
        excludeFromSpending: formData.excludeFromSpending,
      };

      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label htmlFor="category-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Category Name <span className="text-red-500">*</span>
        </label>
        <input
          id="category-name"
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`mt-1 block w-full rounded-lg border ${
            errors.name
              ? 'border-red-300 dark:border-red-700'
              : 'border-gray-300 dark:border-gray-600'
          } bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
          placeholder="e.g., Groceries, Rent, Salary"
          aria-required="true"
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'category-name-error' : undefined}
        />
        {errors.name && (
          <p id="category-name-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.name}
          </p>
        )}
      </div>

      {/* Type */}
      <div>
        <label htmlFor="category-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Type <span className="text-red-500">*</span>
        </label>
        <select
          id="category-type"
          value={formData.type}
          onChange={(e) => handleInputChange('type', e.target.value as CategoryType)}
          className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          aria-required="true"
        >
          <option value={CategoryType.INCOME}>Income</option>
          <option value={CategoryType.EXPENSE}>Expense</option>
          <option value={CategoryType.TRANSFER}>Transfer</option>
        </select>
      </div>

      {/* Parent Category */}
      <div>
        <ParentCategorySelector
          value={formData.parentId}
          onChange={(parentId) => handleInputChange('parentId', parentId)}
          categories={categories}
          categoryType={formData.type}
          excludeCategoryId={isEdit ? category?.id : undefined}
          error={errors.parentId}
        />
      </div>

      {/* Parent Inheritance Section */}
      {parentCategory && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Inherit properties from parent
            </label>
            <input
              type="checkbox"
              checked={inheritFromParent}
              onChange={(e) => setInheritFromParent(e.target.checked)}
              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              aria-label="Inherit properties from parent category"
            />
          </div>

          {inheritFromParent && (
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-3">
                Inheriting from: <span className="font-semibold text-gray-900 dark:text-gray-100">{parentCategory.name}</span>
              </p>
              <div className="grid grid-cols-2 gap-4">
                {/* Color Preview */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Color</p>
                  <div className="flex items-center space-x-2">
                    <span
                      className="h-6 w-6 rounded-md border border-gray-200 dark:border-gray-500 flex-shrink-0"
                      style={{ backgroundColor: parentCategory.color || '#ffffff' }}
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {parentCategory.color ? parentCategory.color.toUpperCase() : 'Not set'}
                    </span>
                  </div>
                </div>

                {/* Icon Preview */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Icon</p>
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const IconComponent = getCategoryIconComponent(parentCategory);
                      return IconComponent ? (
                        <IconComponent className="h-5 w-5 text-gray-700 dark:text-gray-300" aria-hidden="true" />
                      ) : (
                        <span className="text-sm text-gray-400">Not set</span>
                      );
                    })()}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {parentCategory.icon || 'Not set'}
                    </span>
                  </div>
                </div>

                {/* GST Applicable Preview */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">GST Applicable</p>
                  <div className="flex items-center space-x-2">
                    {parentCategory.gstApplicable ? (
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                    ) : (
                      <X className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {parentCategory.gstApplicable ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                {/* Tax Deductible Preview */}
                <div className="space-y-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Tax Deductible</p>
                  <div className="flex items-center space-x-2">
                    {parentCategory.deductible ? (
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                    ) : (
                      <X className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {parentCategory.deductible ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>

                {/* Exclude from Spending Insights Preview */}
                <div className="space-y-1 col-span-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Exclude from spending insights</p>
                  <div className="flex items-center space-x-2">
                    {parentCategory.excludeFromSpending ? (
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                    ) : (
                      <X className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    )}
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {parentCategory.excludeFromSpending ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Color */}
      <ColorPicker
        value={formData.color || ''}
        onChange={(color) => handleInputChange('color', color)}
        label="Color"
        usageCounts={colorUsageCounts}
        disabled={inheritFromParent && !!parentCategory}
      />

      {/* Icon */}
      <IconSelector
        value={formData.icon}
        onChange={(icon) => handleInputChange('icon', icon)}
        label="Icon"
        disabled={inheritFromParent && !!parentCategory}
      />

      {/* GST Applicable */}
      <div className="flex items-center">
        <input
          id="gst-applicable"
          type="checkbox"
          checked={formData.gstApplicable}
          onChange={(e) => handleInputChange('gstApplicable', e.target.checked)}
          disabled={inheritFromParent && !!parentCategory}
          className={`h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 ${
            inheritFromParent && !!parentCategory ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          aria-disabled={inheritFromParent && !!parentCategory}
        />
        <label
          htmlFor="gst-applicable"
          className={`ml-2 block text-sm ${
            inheritFromParent && !!parentCategory
              ? 'text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'text-gray-700 dark:text-gray-300'
          }`}
        >
          GST Applicable
        </label>
      </div>

      {/* Deductible */}
      <div className="flex items-center">
        <input
          id="deductible"
          type="checkbox"
          checked={formData.deductible}
          onChange={(e) => handleInputChange('deductible', e.target.checked)}
          disabled={inheritFromParent && !!parentCategory}
          className={`h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 ${
            inheritFromParent && !!parentCategory ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          aria-disabled={inheritFromParent && !!parentCategory}
        />
        <label
          htmlFor="deductible"
          className={`ml-2 block text-sm ${
            inheritFromParent && !!parentCategory
              ? 'text-gray-500 dark:text-gray-400 cursor-not-allowed'
              : 'text-gray-700 dark:text-gray-300'
          }`}
        >
          Tax Deductible
        </label>
      </div>

      {/* Exclude from Spending Insights */}
      <div className="flex items-start gap-3">
        <input
          id="exclude-from-spending"
          type="checkbox"
          checked={formData.excludeFromSpending}
          onChange={(e) => handleInputChange('excludeFromSpending', e.target.checked)}
          disabled={inheritFromParent && !!parentCategory}
          className={`mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 ${
            inheritFromParent && !!parentCategory ? 'opacity-60 cursor-not-allowed' : ''
          }`}
          aria-describedby="exclude-from-spending-help"
          aria-disabled={inheritFromParent && !!parentCategory}
        />
        <div>
          <label
            htmlFor="exclude-from-spending"
            className={`block text-sm font-medium ${
              inheritFromParent && !!parentCategory
                ? 'text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            Exclude from spending insights
          </label>
          <p id="exclude-from-spending-help" className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            When enabled, this category is hidden from default spending reports and dashboards. Users can still
            include it on demand from the insights card.
          </p>
        </div>
      </div>

      {/* Note */}
      <div>
        <label htmlFor="category-note" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Note
        </label>
        <textarea
          id="category-note"
          value={formData.note}
          onChange={(e) => handleInputChange('note', e.target.value)}
          rows={3}
          className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Optional notes about this category"
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : isEdit ? 'Update Category' : 'Create Category'}
        </button>
      </div>
    </form>
  );
}

